import { useDispatch, useSelector } from 'react-redux';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@wfp-common/hooks/useNavigate';

import {
    loadProductQuantityOptions,
    loadProductUnits,
    loadProductLimitationGroups,
    parkProduct,
    uploadProductImage,
} from '../../apiClient';
import { errorAction } from '../../utils/notifications';
import State from '../../app/store/state';
import { wfpFormat } from '../../utils/utils';
import { Product } from '../../products/helpers';

interface ProductFormData extends Partial<Product> {
    maxAllowedQuantity?: number;
    groups?: number[];
    translation?: string;
    translationEn?: string;
}

export function useAddProduct() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [units, setUnits] = useState([]);
    const [quantityOptions, setQuantityOptions] = useState([]);
    const [limitationGroups, setLimitationGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [product, setProduct] = useState<ProductFormData>({});
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const TEN_MB_BYTES = 10 * 1024 * 1024;

    useEffect(() => {
        Promise.all([loadProductUnits(), loadProductQuantityOptions(), loadProductLimitationGroups()])
            .then(([units, quatityOptions, limitationGroups]) => {
                setUnits(
                    Object.entries(units).map(([key, value]) => ({
                        label: `${value} - ${key}`,
                        value: `${value} - ${key}`,
                    }))
                );
                setQuantityOptions(
                    Object.values(quatityOptions)
                        .filter((val: string) => String(val).toLowerCase() !== 'fixed')
                        .map((val: string) => ({
                            value: wfpFormat(val),
                            label: wfpFormat(val),
                        }))
                );

                const processedGroups = limitationGroups.map((group: { id: number; name: string }) => ({
                    value: group.id,
                    label: group.name,
                }));

                setLimitationGroups(processedGroups);
                setIsLoading(false);
            })
            .catch(() => {
                dispatch(errorAction('Failed to load product options'));
                setIsLoading(false);
            });
    }, [dispatch]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        // Required fields validation
        const missingFields: string[] = [];
        if (!product.category) missingFields.push('Category');
        if (!(product.maxAllowedQuantity === 1) && !product.quantityOption) missingFields.push('Quantity Option');
        if (!product.unit) missingFields.push('Unit');
        if (!product.groups || product.groups.length === 0) missingFields.push('Groups');
        if (!product.name) missingFields.push('Name');

        if (missingFields.length > 0) {
            dispatch(errorAction(`Please fill required fields: ${missingFields.join(', ')}`));
            return;
        }

        try {
            // Prepare product data
            const productData = {
                ...product,
                unit: product.unit?.split(' - ')?.[0]?.trim(),
                quantityOption: product.quantityOption?.toLowerCase?.() || product.quantityOption,
                status: 'active', // Set default status for new products
                maxAllowedQuantity: product.maxAllowedQuantity ? Number(product.maxAllowedQuantity) : undefined,
                groups: product.groups || [],
                translation: product.translation?.trim() || undefined,
                translationEn: product.translationEn?.trim() || undefined,
            };

            // Create the product first
            const createdProduct = await parkProduct(productData as Partial<Product>);

            // If there's an image file and the product was created successfully, upload the image
            if (imageFile && createdProduct?.id) {
                try {
                    await uploadProductImage(createdProduct.id, imageFile);
                } catch (imageError) {
                    // Don't fail the whole process if image upload fails
                    dispatch(errorAction('Product created but image upload failed'));
                }
            }

            // Revoke preview before navigation
            if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
            navigate('/products', { flashSuccess: 'Product added successfully' });
        } catch (error) {
            dispatch(errorAction('Cannot add product'));
        }
    };

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } }) => {
            const { name, value } = (event as { target: { name: string; value: string } }).target;
            setProduct((state) => {
                const fixedLabel = wfpFormat('fixed');
                if (name === 'quantityOption' && state?.maxAllowedQuantity === 1) {
                    return { ...state, quantityOption: fixedLabel };
                }
                return { ...state, [name]: value } as ProductFormData;
            });
        },
        []
    );

    const handleFileChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0] || null;
            if (file && file.size > TEN_MB_BYTES) {
                dispatch(errorAction('Image must be smaller than 10MB'));
                // reset file input and preview
                event.target.value = '';
                setImageFile(null);
                setImagePreviewUrl((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return null;
                });
                return;
            }
            setImageFile(file);
            setImagePreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return file ? URL.createObjectURL(file) : null;
            });
        },
        [dispatch]
    );

    // Bivariant to accept Select's event shape
    const handleMultiSelectChange = useCallback(
        (selectedOptions: { target?: Record<string, unknown> } | Array<{ value: string; label: string }>) => {
            let selectedIds: number[] = [];
            const isEventLike = (v: unknown): v is { target: Record<string, unknown> } =>
                !!v && typeof v === 'object' && 'target' in (v as Record<string, unknown>);

            if (isEventLike(selectedOptions) && selectedOptions.target) {
                const target = selectedOptions.target as Record<string, { value: number } | unknown>;
                const selectedOptionObjects = Object.keys(target)
                    .filter((key) => !isNaN(Number(key)))
                    .map((key) => target[key] as { value: number });
                selectedIds = selectedOptionObjects.map((option) => option.value);
            } else if (Array.isArray(selectedOptions)) {
                selectedIds = selectedOptions.map((o) => Number(o.value));
            }
            setProduct((state) => ({ ...state, groups: selectedIds }));
        },
        []
    );

    const handleMaxAllowedQuantityToggle = useCallback((checked: boolean) => {
        setProduct((state) => {
            const fixedLabel = wfpFormat('fixed');
            const next: ProductFormData = { ...state, maxAllowedQuantity: checked ? 1 : 0 };
            if (checked) {
                next.quantityOption = fixedLabel as string;
            }
            return next;
        });
    }, []);

    const categories = useSelector((state: State) =>
        state.appConfig.entitlementsConfig.categories.map((category) => ({
            value: category,
            label: category,
        }))
    );

    return {
        categories,
        handleSubmit,
        handleChange,
        handleFileChange,
        handleMultiSelectChange,
        handleMaxAllowedQuantityToggle,
        product,
        units,
        quantityOptions,
        limitationGroups,
        isLoading,
        imagePreviewUrl,
    };
}
